import { Box, Typography } from "@mui/material";
import { AlignedGrid, ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { forwardRef, Ref, useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import StandardCellRenderer from "../../components/ag-grid-extensions/renderers/StandardCellRenderer";
import DefaultGrid, { DefaultGridRef } from "../../components/default-grid/DefaultGrid";

export type ReportSectionProps = {
    name: string;
    columnDefs: Array<ColDef>;
    rowData: Array<RowDef>;
    showFooter?: boolean;
    pageSize: number;
    fitToView?: boolean;
    alignedGrids?: Array<AlignedGrid>;
    alignGrids?: boolean;
    onGridReady?: (event: GridReadyEvent) => void;
};

function ReportSection({
    name,
    columnDefs,
    rowData,
    showFooter,
    pageSize,
    fitToView,
    alignedGrids,
    onGridReady,
    alignGrids
}: ReportSectionProps, ref: Ref<DefaultGridRef>): JSX.Element {

    const { t } = useTranslation();
    const [api, setApi] = useState<{ grid: GridApi } | undefined>(undefined);

    useEffect(() => {

        api?.grid.refreshClientSideRowModel("map");

    }, [
        pageSize,
        api,
        alignGrids
    ]);

    useEffect(() => {

        api?.grid.setGridOption("domLayout", fitToView ? "normal" : "autoHeight");
    }, [
        fitToView,
        api
    ]);

    useEffect(() => {
        if (alignGrids && api) {
            api?.grid.autoSizeAllColumns();
        }
    }, [
        api,
        alignGrids
    ]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                flexShrink: 1
            }}>
            <Typography variant="subtitle2" sx={{
                padding: 1,
                backgroundColor: "divider"
            }}>
                {name}
            </Typography>

            {(rowData?.length ?? 0) > 0 ?
                <DefaultGrid
                    onGridReady={(event: GridReadyEvent) => {
                        setApi({
                            grid: event.api
                        });
                        onGridReady && onGridReady(event);
                    }}
                    ref={ref}
                    components={{
                        standardCellRenderer: StandardCellRenderer
                    }}
                    rowData={rowData}
                    defaultColDef={{
                        sortable: true,
                        filter: "agMultiColumnFilter",
                        resizable: true,
                    }}
                    columnDefs={columnDefs}
                    alignedGrids={alignedGrids}
                    pagination={true}
                    paginationPageSize={pageSize}
                    getRowId={data => data.data.rowID}
                    //showFooter={showFooter} //Bug 52553: Reporting - "Include final row with totals" - exists in SCADALynx, but not React. For reporting the totals are included in the data returned so do not slice the rows
                    
                />
                : <Box sx={{
                    padding: 1,
                    borderColor: "var(--ag-border-color, #babfc7)",
                    borderWidth: 1,
                    borderStyle: "solid"
                }}>
                    {t("reporting.messages.noDataFoundForSection")}
                </Box>
            }
        </Box>
    );
}

export default forwardRef<DefaultGridRef, ReportSectionProps>(ReportSection);

